import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Unsubscribe() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        let cancelled = false;
        async function run() {
            if (!token) {
                setStatus("invalid");
                return;
            }
            try {
                const res = await axios.get(`${API}/unsubscribe`, {
                    params: { token },
                });
                if (cancelled) return;
                if (
                    typeof res.data === "string" &&
                    res.data.includes("You're unsubscribed")
                ) {
                    setStatus("done");
                } else {
                    setStatus("done"); // 200 means server marked them unsubbed
                }
            } catch (e) {
                if (cancelled) return;
                setStatus("invalid");
            }
        }
        run();
        return () => {
            cancelled = true;
        };
    }, [token]);

    return (
        <div
            className="min-h-screen bg-bl-bg flex items-center justify-center px-4 py-16"
            data-testid="unsubscribe-page"
        >
            <div className="bl-card max-w-lg w-full p-10 text-center">
                <div className="flex items-center gap-2.5 justify-center mb-7">
                    <div className="bg-bl text-bl-bg w-9 h-9 rounded-lg flex items-center justify-center font-bold text-base">
                        B
                    </div>
                    <span className="font-display font-semibold text-base text-bl">
                        Buyer's Leverage
                    </span>
                </div>

                {status === "loading" && (
                    <div data-testid="unsub-loading">
                        <p className="text-bl-muted text-sm">
                            Processing your unsubscribe…
                        </p>
                    </div>
                )}

                {status === "done" && (
                    <div data-testid="unsub-confirmed">
                        <CheckCircle2 className="h-10 w-10 text-bl mx-auto mb-4" />
                        <h1 className="font-display font-semibold text-2xl text-bl tracking-tight mb-3">
                            You're unsubscribed.
                        </h1>
                        <p className="text-bl-muted text-sm leading-relaxed mb-5">
                            You'll no longer receive marketing emails from
                            Buyer's Leverage. Transactional notifications
                            (report-ready alerts, account changes) will still
                            come through.
                        </p>
                        <p className="text-bl-muted text-xs leading-relaxed">
                            Changed your mind? Email{" "}
                            <a
                                className="text-bl underline"
                                href="mailto:hello@buyersleverage.com"
                            >
                                hello@buyersleverage.com
                            </a>{" "}
                            and we'll re-add you within a day.
                        </p>
                    </div>
                )}

                {status === "invalid" && (
                    <div data-testid="unsub-invalid">
                        <AlertCircle className="h-10 w-10 text-amber-600 mx-auto mb-4" />
                        <h1 className="font-display font-semibold text-2xl text-bl tracking-tight mb-3">
                            Invalid unsubscribe link
                        </h1>
                        <p className="text-bl-muted text-sm leading-relaxed mb-5">
                            We couldn't verify this unsubscribe link. It may
                            be expired or malformed.
                        </p>
                        <p className="text-bl-muted text-xs leading-relaxed">
                            If you need help, email{" "}
                            <a
                                className="text-bl underline"
                                href="mailto:hello@buyersleverage.com"
                            >
                                hello@buyersleverage.com
                            </a>
                            .
                        </p>
                    </div>
                )}

                <div className="mt-7 pt-5 border-t border-bl-line">
                    <Link
                        to="/"
                        className="text-xs text-bl-muted hover:text-bl underline-offset-4 hover:underline"
                        data-testid="unsub-back-home"
                    >
                        ← Back to Buyer's Leverage
                    </Link>
                </div>
            </div>
        </div>
    );
}
